import {
  IWidgetConfigurationProps,
  WithGlobalCss,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { isEqual } from "lodash";
import { h } from "preact";
import { BasicAgGrid, IHeader } from "../../common/components/AgGrid";
import { ApiBackedScComponent } from "../../common/components/ApiBackedScComponent";
import { getTablePptExport } from "../../common/export/table-helper";
import {
  MakeGenericApiCall,
  MakeSalesCreditRoleProfile,
} from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { SCKpis } from "../../common/generic/generic-sc-domain";
import { IGenericRequest } from "../../common/generic/generic-types";
import { ConvertFilter } from "../../common/salescredit/salescredit-filter-help";
import { SCProfileDictionary } from "../../common/salescredit/salescredit-profile-dictionary";
import {
  BuildSalesCreditFilters,
  CreateDateFromFilter,
} from "../../common/salescredit/salescredit-search-criteria";
import { IProps } from "../../common/salescredit/salescredit-types";
import { getMapping } from "../utils/helper";

interface IState {
  firstCall: boolean;
  data: any[];
  headers: IHeader[];
  level?: string;
}

export class SalescreditByTopClientClass extends ApiBackedScComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  private child: any;

  public constructor() {
    super();
    this.setState({
      data: [],
      firstCall: true,
      level: "client_lename",
    });
  }

  public getPptExport() {
    const columnsWidth = [
      2.3,
      0.7,
      0.8,
      0.5,
      0.8,
      0.8,
      0.8,
      0.7,
      0.8,
      0.8,
      0.8,
    ];

    return getTablePptExport(
      this.child._parentComponent.api.gridOptions.api.getDataAsCsv({
        suppressQuotes: true,
        columnSeparator: "¤",
      }),
      columnsWidth
    );
  }

  protected shouldFetchApiData(nextProps: IProps): boolean {
    if (!isEqual(this.props.filters, nextProps.filters)) {
      return true;
    }
    return false;
  }

  protected renderWithDataReady(): JSX.Element {
    return (
      <div style="display:block;height:100%; overflow:hidden">
        <div class="pull-right">
          <input
            type="radio"
            name="level"
            checked={this.state.level === "client_lename"}
            onClick={() => this.onClientLevelChange("client_lename")}
          />{" "}
          SC by Client
          <input
            type="radio"
            name="level"
            checked={this.state.level === "client_grpname"}
            onClick={() => this.onClientLevelChange("client_grpname")}
            style="margin-left: 15px"
          />{" "}
          SC By Client Group
        </div>
        <BasicAgGrid
          ref={instance => {
            this.child = instance != null ? instance._component : instance;
          }}
          onChange={this.props.onChange}
          rowData={this.state.data}
          columnDefs={this.state.headers}
          supressColumnResize={true}
          fileName={"SC per top client"}
          userSelection={this.props.userSelection}
        />
      </div>
    );
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    this.seStateForFirstCall();
    this.setState({ firstCall: false });
    this.setDataStatus("loading");

    const userRole = await MakeSalesCreditRoleProfile(
      props.headers,
      this.props.widgetConfiguration,
      undefined,
      this.state.correlationId
    );

    if (userRole.Role === undefined) {
      this.setDataStatus("no-access-rights");
      this.setStatusText(userRole.StatusText);
      return;
    }
    const filtersConverted = ConvertFilter(props.filters);
    const filters = BuildSalesCreditFilters(filtersConverted);

    const profileDictionary = new SCProfileDictionary();
    const kpis = profileDictionary
      .GetGridFields(userRole.Profile)
      .map(x => x.Kpi);
    const clientLevelId =
      this.state.level === "client_lename" ? "client_leid" : "client_grpid";

    const genericRequest: IGenericRequest = {
      aggregationAttributes: clientLevelId + "," + this.state.level,
      date: CreateDateFromFilter(filtersConverted),
      filter: GenericFilterBuilder(filters),
      kpis: kpis.join(","),
      sort: "-" + profileDictionary.GetKpi(userRole.Profile, SCKpis.SC_YTD),
      topN: 10,
    };

    const responseData = await MakeGenericApiCall(
      props.headers,
      "sc",
      genericRequest,
      this.props.widgetConfiguration,
      this.state.correlationId
    );
    const errorStatus = CheckReponseDataForError(responseData);
    if (errorStatus) {
      this.setDataStatus(errorStatus);
      this.setStatusText(responseData.statusText);
      this.setState({
        data: [],
        headers: [],
      });
      return;
    }
    const mappedDetails = getMapping(
      responseData,
      userRole.Profile,
      this.state.level,
      clientLevelId
    );

    if (mappedDetails !== undefined) {
      this.setState({
        data: mappedDetails.Rows,
        headers: mappedDetails.Headers,
      });
      this.setDataStatus("ready");
    } else {
      this.setState({
        data: [],
        headers: [],
      });
      this.setDataStatus("no-data");
    }
  }
  private seStateForFirstCall() {
    if (
      this.state.firstCall &&
      this.props.userSelection &&
      typeof this.props.userSelection.columns === "string"
    ) {
      const storedState = JSON.parse(this.props.userSelection.columns);
      if (storedState.level !== undefined) {
        this.setState({ level: storedState.level });
      }
    }
  }

  private onClientLevelChange(clientLevel: string) {
    this.setState({ level: clientLevel });
    if (
      this.props.userSelection &&
      typeof this.props.userSelection.columns === "string"
    ) {
      this.onChangeCustomised(this.props.userSelection.columns);
    } else {
      this.onChangeCustomised("{}");
    }
    this.fetchApiData(this.props);
  }

  private onChangeCustomised(value: string) {
    const storedState = JSON.parse(value);
    storedState.level = this.state.level;
    const attrs = JSON.stringify(storedState);
    if (this.props.onChange) {
      this.props.onChange(attrs);
    }
  }
}

export const SalescreditByTopClient = withWidgetConfiguration(
  SalescreditByTopClientClass
);
